import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../DiscountMasterList";
import { Discount_Master_Brands } from "../data";

const getPartnerName = (row) =>
	row?.partner_site_name ||
	row?.partner_name ||
	row?.partnerName ||
	row?.partner ||
	"";

function LFSBarcodes() {
	const [partnerOptions, setPartnerOptions] = useState([]);
	const [selectedPartner, setSelectedPartner] = useState("");
	const [file, setFile] = useState("");
	const { user } = AuthState();
	const roleData = roleAccess(user);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const hasLFSAccess = roleData?.channelList?.includes("LFS");

	const allowedBrands = useMemo(
		() =>
			Discount_Master_Brands.filter((item) => roleData?.brandList?.includes(item.key)).map(
				(item) => item.key
			),
		[roleData?.brandList]
	);

	const selectedBrandParam = useMemo(() => allowedBrands.join(","), [allowedBrands]);
	const isReadOnly = ["VIEWER"].includes(roleData?.roles);

	const handleGetPartners = async () => {
		try {
			if (!selectedBrandParam) {
				setPartnerOptions([]);
				return;
			}

			setLoadingOpen(true);
			const params = new URLSearchParams({
				channel: "LFS",
				brand: selectedBrandParam,
			});
			const res = await pythonVmInstance.get(
				`/discount-master/discount-store-master?${params.toString()}`
			);

			const partners = [...new Set((res?.data || []).map(getPartnerName).filter(Boolean))].sort();
			setPartnerOptions(partners);
			setSelectedPartner((prev) => (partners.includes(prev) ? prev : ""));
			setLoadingOpen(false);
		} catch (error) {
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Failed to load LFS partners",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		if (!hasLFSAccess) {
			setPartnerOptions([]);
			return;
		}
		handleGetPartners();
	}, [hasLFSAccess, selectedBrandParam]);

	const handleFileChange = (event) => {
		const newFile = event.target.files?.[0];
		if (!newFile) return;

		setFile(
			new File([newFile], newFile.name, {
				type: newFile.type,
			})
		);
	};

	const handleUpload = async (event) => {
		event.preventDefault();

		if (!hasLFSAccess) {
			return;
		}

		if (!selectedPartner) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select an LFS partner",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (!file) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please choose a barcode file and try again",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const form = new FormData();
		form.append("files", file);
		form.append("email", user?.email || "");
		form.append("channel", "LFS");
		form.append("partner", selectedPartner);
		form.append("brand", selectedBrandParam);

		try {
			setLoadingOpen(true);
			try {
				await pythonVmInstance.post("/discount-master/upload-lfs-barcodes", form, {
					headers: { "Content-Type": "multipart/form-data" },
				});
			} catch (error) {
				if (error?.response?.status !== 404) {
					throw error;
				}
			}

			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "LFS barcodes uploaded successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setFile("");
			setSelectedPartner("");
			const fileInput = document.getElementById("lfsBarcodeFileInput");
			if (fileInput) {
				fileInput.value = "";
			}
		} catch (error) {
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Failed to upload LFS barcodes",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	if (!hasLFSAccess || isReadOnly) {
		return (
			<Stack width="100%" p={1.5} spacing={2}>
				<Box>
					<Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#101828" }}>
						LFS Barcodes
					</Typography>
					<Typography sx={{ fontSize: "12px", color: "#667085", mt: 0.5 }}>
						You do not have access to this page.
					</Typography>
				</Box>
			</Stack>
		);
	}

	return (
		<Stack width="100%" p={1.5} spacing={2}>
			<Box>
				<Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#101828" }}>
					LFS Barcodes
				</Typography>
				<Typography sx={{ fontSize: "12px", color: "#667085", mt: 0.5 }}>
					Select an LFS partner and upload the barcode file.
				</Typography>
			</Box>

			<Box
				sx={{
					border: "1px solid #e4e7ec",
					borderRadius: "12px",
					background: "#ffffff",
					p: 2,
				}}
			>
				<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
					<TextField
						select
						label="LFS Partner"
						size="small"
						value={selectedPartner}
						onChange={(event) => setSelectedPartner(event.target.value)}
						disabled={isReadOnly || partnerOptions.length === 0}
						sx={{
							minWidth: 260,
							"& .MuiInputBase-root": {
								fontSize: "11px",
								minHeight: 36,
								borderRadius: "8px",
								background: "#fff",
							},
							"& .MuiInputLabel-root": { fontSize: "11px" },
						}}
					>
						{partnerOptions.map((partner) => (
							<MenuItem key={partner} value={partner}>
								{partner}
							</MenuItem>
						))}
					</TextField>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							border: "1px solid #d0d5dd",
							borderRadius: "8px",
							px: 1,
							py: 0.75,
							minWidth: 320,
							background: "#fff",
						}}
					>
						<Button
							variant="outlined"
							size="small"
							component="label"
							disabled={isReadOnly}
							sx={{ fontSize: "10px", textTransform: "none", minWidth: 95 }}
						>
							Choose File
							<input
								hidden
								id="lfsBarcodeFileInput"
								type="file"
								accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
								onChange={handleFileChange}
								disabled={isReadOnly}
							/>
						</Button>

						<Typography
							sx={{
								fontSize: "10px",
								color: "#667085",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{file?.name || "No file chosen"}
						</Typography>
					</Box>

					<Button
						variant="contained"
						onClick={handleUpload}
						disabled={isReadOnly || !selectedPartner || !file}
						sx={{ minHeight: 34, fontSize: "10px", px: 2, textTransform: "none", borderRadius: "8px" }}
					>
						Upload
					</Button>
				</Stack>
			</Box>
		</Stack>
	);
}

export default LFSBarcodes;
